import { useMutation } from "@apollo/client";
import { Draggable } from "@hello-pangea/dnd";
import { withErrorBoundary } from "@sentry/react";
import { Editor } from "@tiptap/core";
import { defer } from "lodash";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  NotesRequirement,
  TopicNodeNewPageFragmentFragment,
  UpdateTopicTitleDescriptionMutationMutation,
  UpdateTopicTitleDescriptionMutationMutationVariables,
} from "types/graphql-schema";

import TopicAssignee from "@apps/topic-assignee/topic-assignee";
import TopicCTA from "@apps/topic-cta/topic-cta";
import TopicDropdownMenu from "@apps/topic-dropdown-menu/topic-dropdown-menu";
import updateTopicMutation from "@apps/topic-sidebar/graphql/update-topic-title-description-mutation";
import { currentUserVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import Error from "@components/error/error";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { focusNextEditor, focusPrevEditor } from "@components/wysiwyg/helpers";
import { topicState } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { isEnterEvent } from "@helpers/helpers";

import createOrUpdateTopicMutation from "../graphql/create-or-update-topic-mutation";
import TopicNodeFragment from "../graphql/topic-node-fragment";
import { focusOnTopicTitleInput, meetingPaddingClassName } from "../helpers";
import DiscussionNotes from "./discussion-notes";
import IndividualNotesContainer from "./individual-notes-container";

export const defaultTopicTitle = "New Topic";

const TopicContent = ({
  snapshot,
  provided,
  topic,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
  index,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  index: number;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
  snapshot?: any;
  provided?: any;
}) => {
  const currentUser = currentUserVar();
  const isSavingRef = useRef(false);
  const canEditTitle = topic.canUpdate.permission && !topic.isMandatory;
  const [editTitle, setEditTitle] = useState(
    topic.title === defaultTopicTitle ? "" : topic.title,
  );
  const isPlaceholderTitle = editTitle === defaultTopicTitle;
  const isTemporaryTopic = String(topic.id) === topic.clientId;

  const [createTopic] = useMutation(createOrUpdateTopicMutation);

  const [saveTopic] = useMutation<
    UpdateTopicTitleDescriptionMutationMutation,
    UpdateTopicTitleDescriptionMutationMutationVariables
  >(updateTopicMutation, {});

  const handleSave = useCallback(() => {
    if (editTitle.trim() === "") {
      if (topic.title !== defaultTopicTitle) {
        setEditTitle(topic.title);
      }
      return;
    }
    if (isSavingRef.current || !canEditTitle || isTemporaryTopic)
      return Promise.resolve();
    isSavingRef.current = true;
    const trimmed = editTitle.trim();
    let savePromise = Promise.resolve();
    if (trimmed && trimmed !== topic.title) {
      savePromise = saveTopic({
        variables: {
          topicId: topic.id,
          title: editTitle,
        },
        optimisticResponse: {
          __typename: "Mutation",
          createOrUpdateTopic: {
            topic: { ...topic, title: editTitle },
            __typename: "CreateOrUpdateTopicMutation",
          },
        },
      }).then(() => {});
    } else {
      setEditTitle(topic.title);
    }
    requestAnimationFrame(() => {
      isSavingRef.current = false;
    });
    return savePromise;
  }, [editTitle, canEditTitle, topic, isTemporaryTopic, saveTopic]);

  const handleCreateTopicBelow = useCallback(() => {
    const clientId = crypto.randomUUID();
    const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
    const topicData = {
      id: clientId,
      clientId,
      title: defaultTopicTitle,
      description: "",
      includesIndividualNotes: isFormalOneonone,
      includesIndividualNotesForCurrentUser: isFormalOneonone,
      includesSharedNotes: !isFormalOneonone,
      discussionNotes: null,
      isMandatory: false,
      canUpdate: { __typename: "PermissionNode", permission: true },
      canDelete: { __typename: "PermissionNode", permission: true },
      created: moment().format(),
      state: topicState.open,
      eventChannelName: `topic-${clientId}`,
      creator: currentUser,
      assignee: currentUser,
      previousTopic: null,
      linkedTemplateTopic: null,
      notesRequirement: null,
      comments: { totalCount: 0, __typename: "CommentNodeConnection" },
      individualNotes: {
        edges: [],
        __typename: "IndividualNoteNodeConnection",
      },
      copiedFrom: null,
      __typename: "TopicNode",
    };
    createTopic({
      variables: {
        meetingId: meeting.id,
        meetingGroupId: meeting.meetingGroup?.id,
        title: topicData.title,
        afterTopicId: topic.id,
        includesIndividualNotes: isFormalOneonone,
        includesSharedNotes: !isFormalOneonone,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: topicData,
          __typename: "CreateOrUpdateTopicMutation",
        },
      },
      update(cache, response) {
        const newTopic = response.data.createOrUpdateTopic.topic;
        const createdTopicCacheNode = cache.writeFragment({
          data: { ...newTopic, clientId },
          fragment: TopicNodeFragment,
          fragmentName: "TopicNodeNewPageFragment",
          variables: { meetingGroupId: meeting.meetingGroup?.id },
        });
        const meetingCacheId = cache.identify(meeting);
        const afterTopicRef = cache.identify({
          __typename: "TopicNode",
          id: topic.id,
        });
        cache.modify({
          id: meetingCacheId,
          fields: {
            topics(cachedTopics) {
              const mergedEdges = cachedTopics.edges.reduce(
                (memo: any[], cachedTopic: any) => {
                  if (cachedTopic.node.__ref === afterTopicRef) {
                    return [
                      ...memo,
                      cachedTopic,
                      {
                        node: createdTopicCacheNode,
                        __typename: "TopicNodeEdge",
                      },
                    ];
                  }
                  return [...memo, cachedTopic];
                },
                [],
              );
              return { edges: uniqBy(mergedEdges, ({ node }) => node.__ref) };
            },
          },
        });
      },
    });
  }, [createTopic, meeting, topic.id, currentUser]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterEvent(e) && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleCreateTopicBelow();
    } else if (isEnterEvent(e)) {
      e.preventDefault();
      // focus on next editable editor
      const container = document.querySelector(
        `#meeting-${meeting.id}-topic-${topic.id}`,
      );
      if (!container) return;
      const editorEls = Array.from(
        container.querySelectorAll(".js-topic-discussion-notes-input"),
      ) as (Element & { editor?: Editor })[];
      const firstActiveEditor = editorEls.find((el) => el.editor?.isEditable);
      if (firstActiveEditor && firstActiveEditor.editor) {
        firstActiveEditor.editor.chain().focus("end").run();
      }
    } else if (e.key === "ArrowDown") {
      const target = e.currentTarget;
      const posBefore = target.selectionStart;
      requestAnimationFrame(() => {
        if (target.selectionStart === posBefore) {
          focusNextEditor();
        }
      });
    } else if (e.key === "ArrowUp") {
      const target = e.currentTarget;
      const posBefore = target.selectionStart;
      requestAnimationFrame(() => {
        if (target.selectionStart === posBefore) {
          focusPrevEditor();
        }
      });
    } else if (e.key === "Escape") {
      setEditTitle(topic.title === defaultTopicTitle ? "" : topic.title);
    }
  };

  let notesHelperText = "";
  if (topic.notesRequirement === NotesRequirement.OneParticipant) {
    notesHelperText = "At least one participant must add notes.";
  } else if (topic.notesRequirement === NotesRequirement.AllParticipants) {
    notesHelperText = "All participants must add notes.";
  }

  // if user has added a topic, it must have a temporary topic id
  // we'll use that to detect new topic and automatically focus on it.
  useEffect(() => {
    if (isTemporaryTopic && topic.title === defaultTopicTitle) {
      defer(() => {
        focusOnTopicTitleInput(topic.id, meeting.id);
      });
    }
  }, [isTemporaryTopic, meeting.id, topic.id, topic.title]);

  const isInFullScreen = index === undefined;
  return (
    <li
      key={topic.clientId}
      id={`meeting-${meeting.id}-topic-${topic.id}`}
      className={classNames(
        "group/topic bg-white",
        "-mx-6",
        topic.isMandatory && "relative js-mandatory-topic",
        snapshot?.isDragging && "bg-purple-50/90 shadow-xl rounded-xl",
      )}
      {...(provided?.draggableProps || {})}
      ref={provided?.innerRef}
      aria-label={`Topic ${topic.title}`}
      data-testid={`container-topic-${topic.id}`}
    >
      <div
        className={classNames(
          "flex w-full relative group pb-2",
          meetingPaddingClassName,
        )}
      >
        <div className="flex-1 flex gap-1">
          {!isInFullScreen && (
            <span
              className={classNames(
                "cursor-grab text-gray-400 absolute py-0.5 px-0 left-[2px] top-1 bg-gray-100 hover:bg-gray-200 rounded hidden sm:group-hover:block",
                snapshot?.isDragging && "block",
                isInExtension && "hidden",
              )}
              {...(provided?.dragHandleProps || {})}
              aria-label="Topic drag handle"
            >
              <TopicflowIcon type="drag" className="h-4 w-4 " />
            </span>
          )}

          {/* TEXT */}
          <div className="flex-1">
            <TextareaAutosize
              key={topic.clientId}
              minRows={1}
              readOnly={!canEditTitle}
              className={classNames(
                "w-full border-none rounded m-0 pr-6 text-base font-medium leading-6 focus:outline-none focus:ring-0 resize-none bg-transparent",
                isInPresentation && "text-xl",
                canEditTitle ? "cursor-text" : "cursor-default",
                isPlaceholderTitle && "text-gray-300",
                "js-meeting-topic-input",
              )}
              disabled={!canEditTitle}
              value={editTitle}
              placeholder={defaultTopicTitle}
              onChange={(e) => {
                setEditTitle(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                handleSave();
              }}
              aria-label={"Topic title"}
            />
          </div>

          {!isInExtension && topic.isMandatory && (
            <div>
              <Tooltip
                text={`The creator of this template has marked the topic as mandatory and it cannot be edited. ${notesHelperText}`}
              >
                <span className="text-xs text-amber-700 bg-amber-100 px-1 py-px rounded-md ml-1">
                  Mandatory
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        {/* STATE & ACTIONS */}
        {/* wrapping div is necessary to keep icon align to top */}
        {!isInExtension && (
          <div className="flex items-start gap-1">
            <div className="invisible group-hover/topic:visible">
              <TopicAssignee
                topic={topic}
                size={ButtonSize.medium}
                assignableUsers={meeting.participants?.edges.map(
                  (edge) => edge?.node?.user,
                )}
              />
            </div>
            <div className="invisible group-hover/topic:visible">
              <TopicDropdownMenu
                meetingGroup={meeting.meetingGroup}
                topic={topic}
                meeting={meeting}
                portal={opensInSidebar}
                currentPageMeeting={currentPageMeeting}
                isLastTopic={meeting.topics?.edges.length === 1}
                size={ButtonSize.medium}
              />
            </div>
            <TopicCTA topic={topic} />
          </div>
        )}
      </div>
      <div className="relative">
        {topic.includesSharedNotes && (
          <DiscussionNotes
            topic={topic}
            meeting={meeting}
            className={isInPresentation ? "text-lg" : ""}
            onModEnter={handleCreateTopicBelow}
          />
        )}
        <div
          className={classNames(
            "grid transition-[grid-template-rows] duration-200 ease-in-out",
            topic.includesIndividualNotes ||
              topic.includesIndividualNotesForCurrentUser
              ? "grid-rows-[1fr]"
              : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className="pb-10">
              <IndividualNotesContainer
                topic={topic}
                meeting={meeting}
                className={meetingPaddingClassName}
                onModEnter={handleCreateTopicBelow}
              />
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

const Topic = ({
  topic,
  index,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  index: number;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
}) => {
  const isInFullScreen = index === undefined;

  return isInFullScreen ? (
    <TopicContent
      key={topic.clientId}
      topic={topic}
      index={index}
      meeting={meeting}
      opensInSidebar={opensInSidebar}
      isInExtension={isInExtension}
      isInPresentation={isInPresentation}
      currentPageMeeting={currentPageMeeting}
    />
  ) : (
    <Draggable draggableId={`topic-${topic.id}`} index={index}>
      {(provided, snapshot) => (
        <TopicContent
          provided={provided}
          snapshot={snapshot}
          isInPresentation={isInPresentation}
          key={topic.clientId}
          topic={topic}
          index={index}
          meeting={meeting}
          opensInSidebar={opensInSidebar}
          isInExtension={isInExtension}
          currentPageMeeting={currentPageMeeting}
        />
      )}
    </Draggable>
  );
};

const TopicWithSentry = withErrorBoundary(Topic, {
  fallback: <Error description={"The topic could not be rendered."} />,
});

export default TopicWithSentry;
